import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'

type Environment = 'sandbox' | 'live'

const STORAGE_KEY = 'sa_environment'

interface OperationEnvironmentState {
  environment: Environment
  setEnvironment: (env: Environment) => void
  isLive: boolean
}

const OperationEnvironmentContext = createContext<OperationEnvironmentState>({
  environment: 'sandbox',
  setEnvironment: () => {},
  isLive: false,
})

function loadEnv(): Environment {
  if (typeof window === 'undefined') return 'sandbox'
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'live' ? 'live' : 'sandbox'
  } catch {
    return 'sandbox'
  }
}

export const OperationEnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [environment, setEnvState] = useState<Environment>(loadEnv)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, environment)
  }, [environment])

  const setEnvironment = useCallback((env: Environment) => {
    setEnvState(env)
  }, [])

  return (
    <OperationEnvironmentContext.Provider
      value={{ environment, setEnvironment, isLive: environment === 'live' }}
    >
      {children}
    </OperationEnvironmentContext.Provider>
  )
}

export function useOperationEnvironment() {
  return useContext(OperationEnvironmentContext)
}
